import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import {
  AsanaLinkUpdate,
  AsanaProjectList,
  ChatUatValidationJobResponse,
  ChatUatValidationRun,
  ChatUatValidationState,
  ProjectValidationRunSummary,
  ProjectValidationStage,
  Stage,
} from "../lusca-search-api/generated-src";
import { ChatUatApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";
import useAsanaAuthorization, {
  isAsanaAuthorizationError,
} from "./useAsanaAuthorization";

const PENDING_JOB_STATUSES = new Set(["pending", "running"]);
const MAX_TRANSIENT_POLL_RETRIES = 3;
const MAX_TRANSIENT_POLL_RETRY_DELAY_MS = 5000;

function isTransientValidationPollError(error: unknown): boolean {
  if (!axios.isAxiosError(error) || axios.isCancel(error)) {
    return false;
  }
  const status = error.response?.status;
  return (
    status === undefined || status === 408 || status === 429 || status >= 500
  );
}

export function shouldRetryValidationPoll(
  failureCount: number,
  error: unknown
): boolean {
  return (
    failureCount < MAX_TRANSIENT_POLL_RETRIES &&
    isTransientValidationPollError(error)
  );
}

export function validationPollRetryDelay(attemptIndex: number): number {
  return Math.min(1000 * 2 ** attemptIndex, MAX_TRANSIENT_POLL_RETRY_DELAY_MS);
}

export function validationRunsForProject(
  runs: ProjectValidationRunSummary[],
  projectGid: string | undefined
): ProjectValidationRunSummary[] {
  return projectGid
    ? runs.filter((run) => run.asanaProjectGid === projectGid)
    : [];
}

export function shouldAdoptValidationJob(
  activeJobId: string | undefined,
  serverJobId: string | undefined,
  pausedJobId: string | undefined,
  terminalJobId: string | undefined
): boolean {
  return Boolean(
    !activeJobId &&
      serverJobId &&
      serverJobId !== pausedJobId &&
      serverJobId !== terminalJobId
  );
}

function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const value: unknown = error.response?.data;
    if (
      typeof value === "object" &&
      value !== null &&
      "error" in value &&
      typeof (value as { error?: unknown }).error === "string"
    ) {
      return (value as { error: string }).error;
    }
  }
  return "UAT Insights could not complete this request.";
}

function validationStage(stage: Stage): ProjectValidationStage {
  return stage.toLowerCase() as ProjectValidationStage;
}

export default function useUatProjectValidation(
  stage: Stage,
  loadProjects = false
) {
  const api = useMemo(() => ChatUatApiFactory(), []);
  const queryClient = useQueryClient();
  const asanaAuthorization = useAsanaAuthorization();
  const [selectedRunId, setSelectedRunId] = useState<string>();
  const [activeJobId, setActiveJobId] = useState<string>();
  const [pausedJobId, setPausedJobId] = useState<string>();
  const [terminalJobId, setTerminalJobId] = useState<string>();
  const [lastJob, setLastJob] = useState<ChatUatValidationJobResponse>();
  const [jobError, setJobError] = useState<string>();

  const stateQuery = useQuery<ChatUatValidationState, Error>(
    ["chat-uat-validation"],
    async () => (await api.getChatUatValidationState()).data
  );
  const state = stateQuery.data;
  const linkedProjectGid = state?.asanaLink?.projectGid;
  const runs = useMemo(
    () => validationRunsForProject(state?.runs ?? [], linkedProjectGid),
    [linkedProjectGid, state?.runs]
  );
  const projectsQuery = useQuery<AsanaProjectList, Error>(
    ["chat-uat-asana-projects"],
    async () => (await api.listChatUatAsanaProjects()).data,
    {
      enabled:
        asanaAuthorization.isAuthorized &&
        state !== undefined &&
        (loadProjects || !state.asanaLink),
      onError: (error) => {
        if (isAsanaAuthorizationError(error)) {
          asanaAuthorization.markUnauthorized();
        }
      },
    }
  );

  useEffect(() => {
    const serverJobId = state?.activeValidation?.jobId;
    if (
      serverJobId &&
      shouldAdoptValidationJob(
        activeJobId,
        serverJobId,
        pausedJobId,
        terminalJobId
      )
    ) {
      setActiveJobId(serverJobId);
    }
  }, [activeJobId, pausedJobId, state?.activeValidation?.jobId, terminalJobId]);

  useEffect(() => {
    const selectedRunIsCurrent = Boolean(
      selectedRunId && runs.some((run) => run.runId === selectedRunId)
    );
    if (!selectedRunIsCurrent) {
      const nextRunId = runs[0]?.runId;
      if (selectedRunId !== nextRunId) {
        setSelectedRunId(nextRunId);
      }
    }
  }, [runs, selectedRunId]);

  const runQuery = useQuery<ChatUatValidationRun, Error>(
    ["chat-uat-validation-run", selectedRunId],
    async () =>
      (await api.getChatUatScopeValidationRun(selectedRunId as string)).data,
    {
      enabled: Boolean(selectedRunId),
      keepPreviousData: true,
    }
  );
  const selectedRun =
    selectedRunId &&
    runQuery.data?.run.runId === selectedRunId &&
    linkedProjectGid &&
    runQuery.data.run.asanaProjectGid === linkedProjectGid
      ? runQuery.data
      : undefined;

  const refreshValidationState = async () => {
    await queryClient.invalidateQueries(["chat-uat-validation"]);
  };

  const handleJobResult = (job: ChatUatValidationJobResponse) => {
    setLastJob(job);
    if (PENDING_JOB_STATUSES.has(job.status)) {
      setJobError(undefined);
      setActiveJobId(job.jobId);
      return;
    }

    setTerminalJobId(job.jobId);
    setActiveJobId(undefined);
    setPausedJobId(undefined);
    setJobError(
      job.status === "completed"
        ? undefined
        : job.error ?? "Project validation did not complete."
    );
    (async () => {
      await refreshValidationState();
      if (job.status === "completed") {
        setSelectedRunId(job.jobId);
        await queryClient.invalidateQueries([
          "chat-uat-validation-run",
          job.jobId,
        ]);
      }
    })().catch((error: unknown) => setJobError(errorMessage(error)));
  };

  const jobQuery = useQuery<ChatUatValidationJobResponse, Error>(
    ["chat-uat-validation-job", activeJobId],
    async () =>
      (await api.getChatUatScopeValidationJob(activeJobId as string)).data,
    {
      enabled: Boolean(activeJobId),
      retry: shouldRetryValidationPoll,
      retryDelay: validationPollRetryDelay,
      refetchInterval: (data) =>
        data && PENDING_JOB_STATUSES.has(data.status)
          ? data.retryAfterMs ?? 1000
          : false,
      onSuccess: handleJobResult,
      onError: (error) => {
        if (isAsanaAuthorizationError(error)) {
          asanaAuthorization.markUnauthorized();
        }
        setPausedJobId(activeJobId);
        setActiveJobId(undefined);
        setJobError(errorMessage(error));
      },
    }
  );

  const saveLinkMutation = useMutation<
    ChatUatValidationState,
    Error,
    AsanaLinkUpdate
  >(async (link) => (await api.updateChatUatAsanaScope(link)).data, {
    onSuccess: (updatedState) => {
      queryClient.setQueryData(["chat-uat-validation"], updatedState);
    },
    onError: (error) => {
      if (isAsanaAuthorizationError(error)) {
        asanaAuthorization.markUnauthorized();
      }
    },
  });

  const disconnectMutation = useMutation<ChatUatValidationState, Error, void>(
    async () => {
      if (!state) {
        throw new Error("Validation state is not loaded");
      }
      return (await api.deleteChatUatAsanaScope(state.version)).data;
    },
    {
      onSuccess: (updatedState) => {
        asanaAuthorization.markAuthorized();
        queryClient.setQueryData(["chat-uat-validation"], updatedState);
      },
    }
  );

  const startMutation = useMutation<ChatUatValidationJobResponse, Error, void>(
    async () => {
      if (!state) {
        throw new Error("Validation state is not loaded");
      }
      return (
        await api.startChatUatScopeValidation({
          jobId: uuidv4(),
          stage: validationStage(stage),
          version: state.version,
        })
      ).data;
    },
    {
      onMutate: () => {
        setPausedJobId(undefined);
        setTerminalJobId(undefined);
        setJobError(undefined);
        setLastJob(undefined);
      },
      onSuccess: handleJobResult,
      onError: (error) => {
        if (isAsanaAuthorizationError(error)) {
          asanaAuthorization.markUnauthorized();
        }
        setJobError(errorMessage(error));
        refreshValidationState().catch(() => undefined);
      },
    }
  );

  const selectRun = (runId: string) => {
    setSelectedRunId(runId);
  };

  const resumeValidation = () => {
    const jobId = state?.activeValidation?.jobId;
    if (!jobId) {
      return;
    }
    setJobError(undefined);
    setLastJob(undefined);
    setPausedJobId(undefined);
    setActiveJobId(jobId);
  };

  return {
    state,
    runs,
    selectedRunId,
    selectedRun,
    job: jobQuery.data ?? lastJob,
    jobError,
    isLoading: stateQuery.isLoading || runQuery.isLoading,
    isRefreshing: stateQuery.isFetching,
    isStarting: startMutation.isLoading,
    isPolling: Boolean(activeJobId),
    isAsanaAuthorized: asanaAuthorization.isAuthorized,
    asanaAuthorizationRequired: asanaAuthorization.needsReconnect,
    isConnectingAsana: asanaAuthorization.isConnecting,
    asanaAuthorizationError: asanaAuthorization.authorizationError,
    projects: projectsQuery.data?.projects ?? [],
    projectsError: projectsQuery.error
      ? errorMessage(projectsQuery.error)
      : undefined,
    isLoadingProjects: projectsQuery.isLoading || projectsQuery.isFetching,
    isSavingLink: saveLinkMutation.isLoading,
    isDisconnecting: disconnectMutation.isLoading,
    saveLinkError: saveLinkMutation.error
      ? errorMessage(saveLinkMutation.error)
      : undefined,
    disconnectError: disconnectMutation.error
      ? errorMessage(disconnectMutation.error)
      : undefined,
    error:
      stateQuery.error || runQuery.error
        ? errorMessage(stateQuery.error ?? runQuery.error)
        : undefined,
    refreshAsanaProjects: projectsQuery.refetch,
    saveAsanaLink: saveLinkMutation.mutateAsync,
    disconnectAsana: disconnectMutation.mutateAsync,
    beginAsanaAuthorization: asanaAuthorization.beginAuthorization,
    startValidation: startMutation.mutateAsync,
    resumeValidation,
    canResumeValidation: Boolean(
      state?.activeValidation?.jobId &&
        state.activeValidation.jobId === pausedJobId
    ),
    selectRun,
    refresh: refreshValidationState,
  };
}
